import api from '@/api/axios';
import { useCallback } from 'react';
import { Button } from '@/components/ui/therapist/Button';
import { Modal } from '@/components/ui/therapist/Modal';
import { API_NAME_ACCORD_TYPE } from '@/views/SetCard/constants';
import { SetType } from '@/stores/store.types';
import { Text } from '@/components/Text';
import encryptedStorage from '@/stores/encryptedStorage';
import { setStorageKey } from '@/utils/setStorageKey';
import { logError } from '@/utils/logHandling';

type DeleteModalState = { show: boolean; isSetActive: boolean };

type DeleteModalProps = {
  data: any;
  deleteModal: {
    show: boolean;
    isSetActive: boolean;
  };
  fetchPatientsWrapper: () => void;
  patientID: string;
  refreshPatient: () => void;
  selectedPatientFullName: string;
  setDeleteModal: (
    value: DeleteModalState | ((prev: DeleteModalState) => DeleteModalState),
  ) => void;
  type: SetType;
};

const DeleteModalDataAccordingToSetType = {
  Wörtersets: {
    title: 'Wörterset löschen',
    activeSetText: (patientName: string) => {
      return `ACHTUNG! Sie sind dabei ein aktives Wörterset zu löschen! Danach kann ${patientName} das Wörterset nicht mehr im Eigentraining verwenden. Das Löschen des Wörtersets inklusive dessen Ergebnisse ist unwiderruflich!`;
    },
    bodyText:
      'Das Löschen des Wörtersets inklusive dessen Ergebnisse ist unwiderruflich!',
    confirmText: 'Unwiderruflich löschen',
    cancelText: 'Abbrechen',
  },
  Sätzesets: {
    title: 'Sätzeset löschen',
    activeSetText: (patientName: string) => {
      return `ACHTUNG! Sie sind dabei ein aktives Sätzeset zu löschen! Danach kann ${patientName} das Sätzeset nicht mehr im Eigentraining verwenden. Das Löschen des Sätzesets inklusive dessen Ergebnisse ist unwiderruflich!`;
    },
    bodyText:
      'Das Löschen des Sätzesets inklusive dessen Ergebnisse ist unwiderruflich!',
    confirmText: 'Unwiderruflich löschen',
    cancelText: 'Abbrechen',
  },
  Textesets: {
    title: 'Texteset löschen',
    activeSetText: (patientName: string) => {
      return `ACHTUNG! Sie sind dabei ein aktives Texteset zu löschen! Danach kann ${patientName} das Texteset nicht mehr im Eigentraining verwenden. Das Löschen des Textesets ist unwiderruflich!`;
    },
    bodyText:
      'Das Löschen des Textesets inklusive dessen Ergebnisse ist unwiderruflich!',
    confirmText: 'Unwiderruflich löschen',
    cancelText: 'Abbrechen',
  },
};

export const DeleteModal = ({
  data,
  deleteModal,
  fetchPatientsWrapper,
  patientID,
  refreshPatient,
  selectedPatientFullName,
  setDeleteModal,
  type,
}: DeleteModalProps) => {
  const deleteModalData = DeleteModalDataAccordingToSetType[type];

  const handleDeletePress = useCallback(async () => {
    try {
      await api.delete(
        `/api/patients/${patientID}/${API_NAME_ACCORD_TYPE[type]}/${data?._id}`,
        data,
      );
      await encryptedStorage().removeItem(setStorageKey(data?._id, type));
      fetchPatientsWrapper();
      refreshPatient();
      setDeleteModal({
        show: false,
        isSetActive: false,
      });
    } catch (error) {
      logError('Could not delete patient', error);
    }
  }, []);

  return (
    <Modal
      isVisible={deleteModal.show}
      onClose={() =>
        setDeleteModal((prev) => ({
          ...prev,
          show: false,
        }))
      }
      cancelActionButton={
        <Button
          label={deleteModalData.cancelText}
          look="secondary"
          onPress={() =>
            setDeleteModal((prev) => ({
              ...prev,
              show: false,
            }))
          }
        />
      }
      confirmActionButton={
        <Button
          testID="set-delete-confirm-button"
          label={deleteModalData.confirmText}
          look="danger"
          onPress={handleDeletePress}
        />
      }
      title={deleteModalData.title}
    >
      <Text style={{ fontSize: 20 }}>
        {deleteModal.isSetActive
          ? deleteModalData.activeSetText(selectedPatientFullName)
          : deleteModalData.bodyText}
      </Text>
    </Modal>
  );
};
